import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { CodeNode, $createCodeNode, CodeHighlightNode } from '@lexical/code';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';

import { $getRoot, $createTextNode } from 'lexical';

import 'prismjs';
import 'prismjs/themes/prism.css';
import 'prismjs/components/prism-sql';

import { CodeHighlightPlugin, EditorOnReadyPlugin } from '../../../../../components';

const StyledContentEditable = styled(ContentEditable)`
  color: var(--content-body);
  font-family: var(--font-family-mono);
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5; /* 18px */
  letter-spacing: -0.02em;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 8px;
  min-height: 100px;
  outline: none;
  user-select: text;
  -webkit-user-select: text;
  -moz-user-select: text;
  -ms-user-select: text;

  &:focus {
    border-color: var(--primary-color);
  }
`;

const LexicalCodeBlock = ({
  code,
  onChange,
  isEditing = false,
  language = 'sql',
  editorRef: externalEditorRef
}) => {
  const internalEditorRef = useRef(null);
  const editorRef = externalEditorRef || internalEditorRef;

  const initialConfig = {
    namespace: 'CodeBlock',
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Lexical Error:', error);
    },
    editable: true,
    nodes: [CodeNode, CodeHighlightNode],
    theme: {
      code: 'editor-code',
      codeHighlight: {
        atrule: 'token attr-rule',
        attr: 'token attr-name',
        boolean: 'token boolean',
        builtin: 'token builtin',
        cdata: 'token cdata',
        char: 'token char',
        class: 'token class-name',
        comment: 'token comment',
        constant: 'token constant',
        deleted: 'token deleted',
        doctype: 'token doctype',
        entity: 'token entity',
        function: 'token function',
        important: 'token important',
        inserted: 'token inserted',
        keyword: 'token keyword',
        namespace: 'token namespace',
        number: 'token number',
        operator: 'token operator',
        prolog: 'token prolog',
        property: 'token property',
        punctuation: 'token punctuation',
        regex: 'token regex',
        selector: 'token selector',
        string: 'token string',
        symbol: 'token symbol',
        tag: 'token tag',
        url: 'token url',
        variable: 'token variable'
      }
    }
  };

  const handleChange = (editorState) => {
    editorState.read(() => {
      const root = $getRoot();
      const text = root.getTextContent();
      if (onChange) onChange(text);
    });
  };

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.setEditable(isEditing);
      editorRef.current.update(() => {
        const root = $getRoot();
        root.clear();
        const codeNode = $createCodeNode();
        codeNode.setLanguage(language);
        codeNode.append($createTextNode(code));
        root.append(codeNode);
      });
      if (isEditing) {
        editorRef.current.focus();
      }
    }
  }, [code, language, isEditing]);

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className='editor-container'>
        <RichTextPlugin
          contentEditable={<StyledContentEditable />}
          placeholder={null}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <CodeHighlightPlugin />
        <HistoryPlugin />
        <EditorOnReadyPlugin
          onReady={(editor) => {
            editorRef.current = editor;
            editor.setEditable(isEditing);
            if (isEditing) {
              editor.focus();
            }
          }}
        />
        <OnChangePlugin onChange={handleChange} />
      </div>
    </LexicalComposer>
  );
};

LexicalCodeBlock.propTypes = {
  code: PropTypes.string.isRequired,
  language: PropTypes.string,
  onChange: PropTypes.func,
  isEditing: PropTypes.bool,
  editorRef: PropTypes.shape({
    current: PropTypes.shape({
      setEditable: PropTypes.func.isRequired,
      update: PropTypes.func.isRequired,
      focus: PropTypes.func.isRequired
    })
  })
};

export { LexicalCodeBlock };
